import React from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import { useScreenType } from '../../hooks/useScreenType';

import DesktopUpgrade from './DesktopUpgrade';
import MobileUpgrade from './MobileUpgrade';
import { getPricingPlans } from '../../config/pricingPlans';

const UpgradeScreen: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isMobile } = useScreenType();

    const [promoCode, setPromoCode] = React.useState('');
    const [isApplying, setIsApplying] = React.useState(false);
    const [promoError, setPromoError] = React.useState<string | null>(null);
    const [promoSuccess, setPromoSuccess] = React.useState<string | null>(null);
    const [paymentLoading, setPaymentLoading] = React.useState(false);

    React.useEffect(() => {
        const trackVisit = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (token) {
                    await fetch(`${process.env.REACT_APP_API_URL}/v1/users/track-payment-visit`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                }
            } catch (err) {
                console.error('Failed to track payment page visit', err);
            }
        };
        trackVisit();
    }, []);

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return;

        setIsApplying(true);
        setPromoError(null);
        setPromoSuccess(null);

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/promo/apply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ code: promoCode })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Erreur lors de l\'application du code');
            }

            setPromoSuccess(`Félicitations ! Votre compte a été mis à jour vers le plan ${data.role?.name || 'Pro'}.`);
            setPromoCode('');

            if (token) {
                // Delay reload to let the user see the message
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            }
        } catch (err: any) {
            setPromoError(err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const handlePayment = async (planId: string) => {
        if (!planId || planId === 'gratuit') return;

        setPaymentLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ plan_id: planId })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Erreur lors de la création du paiement');
            }

            // Redirect the user to PayDunia payment page
            window.location.href = data.payment_url;
        } catch (err: any) {
            // Friendly error message for payment failures
            const friendlyMessage = `Un problème est survenu lors de la préparation de votre paiement.\n\nVeuillez réessayer plus tard ou contacter notre équipe à contact@kaneabourse.com si le problème persiste.`;
            alert(friendlyMessage);
            console.error('Payment error:', err);
        } finally {
            setPaymentLoading(false);
        }
    };

    const plans = getPricingPlans(user);

    const childProps = {
        plans, promoCode, setPromoCode, isApplying, promoError, promoSuccess,
        handleApplyPromo, handlePayment, paymentLoading,
        navigate: (path: string) => navigate(path),
    };

    return (
        <SiteLayout>
            <Header icon='workspace_premium' title='Passer à la version Pro' />
            {isMobile ? (
                <MobileUpgrade {...childProps} />
            ) : (
                <DesktopUpgrade {...childProps} />
            )}
        </SiteLayout>
    );
};

export default UpgradeScreen;
